import React, { Suspense, useContext, useState } from "react";

import Alert from "../components/alert.jsx";
import CheckBoxes from "../components/checkboxes.jsx";
import HelperText from "../components/helper-text.jsx";
import RadioButtons from "../components/radiobuttons.jsx";
import Select from "../components/select.jsx";
import TextArea from "../components/textarea.jsx";
import TextField from "../components/textfield.jsx";
import SubmissionContext from "../utilities/submission-context";

/**
 * @typedef {import('../../types.js').Component} Component
 * @typedef {import('../../types.js').showValidation} showValidation
 * @typedef {import('react').SetStateAction<any>} SetStateAction
 * @typedef {import('react').Dispatch<SetStateAction>} Dispatch
 */

/**
 * @typedef {object} FormGroupProps
 * @property {Component} component - The component to render
 * @property {string} componentId - The componentId for the form group
 * @property {Function} clearResponse - The clearResponse function for the form group
 * @property {boolean} showSubmissionError - The showSubmissionError boolean for the form group
 * @property {string} helpText - The helpText for the form group
 * @property {string} value - The value for the form group
 * @property {boolean} showIncompleteFeedback - your not done boy
 */

/**
 *
 * @param {Component} component - form component
 * @param {showValidation} showValidation - whether to show validation
 * @returns {React.ReactNode} | null} - element
 */
function renderComponent(component, showValidation) {
  const components = {
    text: () => <HelperText component={component} />,
    textfield: () => (
      <TextField component={component} showValidation={showValidation} />
    ),
    textarea: () => (
      <TextArea component={component} showValidation={showValidation} />
    ),
    select: () => (
      <Select component={component} showValidation={showValidation} />
    ),
    radiobuttons: () => (
      <RadioButtons component={component} showValidation={showValidation} />
    ),
    checkboxes: () => (
      <CheckBoxes component={component} showValidation={showValidation} />
    ),
  };
  const validComponents = Object.keys(components);
  if (!validComponents.includes(component.type)) {
    console.error(`Invalid component type: ${component.type}`);
    return null;
  }
  return components[component.type]();
}

/**
 * @typedef {object} GroupValidation
 * @property {boolean} isValid - whether is valid input or not
 * @property {string} feedbackMessage - the feedback message
 * @property {boolean} hasResponse - whether there is a response or not
 */

/**
 * Renders a form group. This is a wrapper component that renders the appropriate component based on the type prop.
 * @param {FormGroupProps} props - props
 * @returns {React.JSX.Element} React function component
 */
function Fieldset({ component, showIncompleteFeedback }) {
  const { showCorrectResponseFeedback = true, type } = component;
  const { submissionData, setSubmissionData } = useContext(SubmissionContext);
  const { componentId, helpText } = component;
  const [groupValidation, setGroupValidation] = useState(
    /** @type {{ isValid: boolean, feedbackMessage: string }?}  */ null,
  );

  /**
   *  showValidation
   * @param {boolean} isValid - whether is valid input or not
   * @param {string} key - the feedback key
   * @param {any} value - response value
   * @returns {void}
   */
  function showValidation(isValid, key, value) {
    const feedbackMessage = component.feedback[key];

    // destructure components from the submissionData, setting default as an array if none exist yet
    const { components = [] } = submissionData;

    setGroupValidation({ isValid, feedbackMessage });

    const componentToUpdate = components?.find(
      (item) => item.componentId === componentId,
    );

    if (componentToUpdate) {
      componentToUpdate.response = value;
    }
    if (!componentToUpdate) {
      components?.push({
        componentId,
        response: value,
      });
    }

    setSubmissionData({
      ...submissionData,
      components: [...components],
    });
  }
  const showHelpText = helpText && showIncompleteFeedback && !groupValidation;

  return (
    <fieldset data-testid={component.componentId}>
      {showHelpText && (
        <div className="fr-rounded-md fr-p-2 fr-max-w-sm fr-bg-primary-100 fr-text-primary-800 fr-flex fr-items-center fr-mb-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="fr-w-6 fr-h-6 fr-mr-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
            />
          </svg>
          <small className="fr-text-sm fr-text-primary-800">{helpText}</small>
        </div>
      )}
      <Suspense
        fallback={
          <div id="component-suspense-fallback">Loading component...</div>
        }
      >
        {renderComponent(component, showValidation)}
      </Suspense>
      {/* Only show feedback if there's a message. Checkboxes need to render their own feedback because they're oddballs */}
      {component.validation.required &&
        showCorrectResponseFeedback &&
        groupValidation?.feedbackMessage &&
        type !== "checkboxes" && (
          <Alert
            message={groupValidation?.feedbackMessage}
            type={groupValidation.isValid ? "success" : "error"}
          />
        )}
    </fieldset>
  );
}

export default Fieldset;
